import { Component, OnDestroy, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatSelect } from '@angular/material/select';
import { AlertController, ModalController, ToastController } from '@ionic/angular';
import { Subject } from 'rxjs/internal/Subject';
import { finalize, map, switchMap, take, takeUntil } from 'rxjs/operators';
import { UploadDocument } from 'src/app/core/model/application.model';
import { InvestorDocument, TransferDocument } from 'src/app/core/model/document.model';
import { responseModel } from 'src/app/core/model/response.model';
import { ApplicationService } from 'src/app/services/application/application.service';
import { CommonService } from 'src/app/services/common/common.service';
import { UploadService } from 'src/app/services/upload/upload.service';

@Component({
  selector: 'app-wet-sign-modal',
  templateUrl: './wet-sign-modal.component.html',
  styleUrls: ['./wet-sign-modal.component.scss'],
})
export class WetSignModalComponent implements OnInit, OnDestroy {

  //#region Fields
  title = '';
  formId: string = '';
  pdfPageNo: number = 0;

  childFundId: string = '';
  applicationId: number = 0;
  pan: string = '';

  investorId: number = 0;
  transferId: number = 0;
  uploadedDocuments: Array<TransferDocument> = [];
  transferApplication: string = '';

  sameDocumentTypeError = false;
  isLoading = false;

  requiredDocuments: Array<any> = [
    { id: 1, name: 'Application Form', value: 'Application Form', isMandatory: true },
    { id: 2, name: 'Deed of Adherence', value: 'Deed of Adherence', isMandatory: true },
    { id: 3, name: 'Seller Letter', value: 'Seller Letter', isMandatory: true },
    { id: 4, name: 'Buyer Letter', value: 'Buyer Letter', isMandatory: true },
  ]

  private destroy$ = new Subject<void>();

  @ViewChild('confirmSubmission') confirmSubmissionModal!: TemplateRef<any>;
  dialogRef!: MatDialogRef<any>;
  //#endregion

  constructor(
    private modalController: ModalController,
    private toastController: ToastController,
    private uploadService: UploadService,
    private applicationService: ApplicationService,
    private commonService: CommonService,
    private alertController: AlertController,
    private dialog: MatDialog
  ) { }

  //#region Life Cycle Hook
  ngOnInit() {
    this.loadOfflineDocuments();
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }
  //#endregion

  //#region Public Methods
  dismissModal() {
    this.modalController.dismiss();
  }

  /**
   * Upload file to S3 bucket and save the document details in the database
   * @param fileRef: any - File input reference
   * @param type: MatSelect - Document type selected by the user
  */
  async uploadFile(fileRef: any, type: MatSelect) {

    this.sameDocumentTypeError = false;
    const file = fileRef.files[0];
    fileRef.value = '';

    const idxDot = file ? file.name.lastIndexOf('.') + 1 : null;
    const extFile = file
      ? file.name.substr(idxDot, file.name.length).toLowerCase()
      : null;

    const docType = type.value;
    type.value = '';

    if (this.uploadedDocuments.some(d => d.documentType === docType)) {
      this.sameDocumentTypeError = true;
      return;
    }

    if (!file) {
      const toast = await this.toastController.create({
        message: 'Please upload a file to continue',
        duration: 2000,
      });
      toast.present();
      return;
    }

    if (this.uploadedDocuments.some(d => d.documentName === file.name)) {
      const toast = await this.toastController.create({
        message: 'This document already uploaded, please upload some other document',
        duration: 2000,
      });
      toast.present();
      return;
    }

    const validationResult = this.uploadService.validatingOfflineFilesUpload(file, docType);

    if (validationResult) {

      const toast = await this.toastController.create({
        message: validationResult,
        duration: 2000,
      });
      toast.present();
      return;
    }

    if (docType !== 'Application Form') {

      const body = {} as UploadDocument;

      body.applicationUuid = this.applicationId.toString();
      body.transferApplication = this.transferApplication ?? '';
      body.transferId = this.transferId.toString() ?? "0";
      body.documentType = docType;
      this.isLoading = true;

      this.commonService.encrypt(this.applicationId).pipe(
        switchMap((res: any) => {
          const investmentId = res.encryptedText.replaceAll('/', 'slh').replaceAll('==', '--').replaceAll('=', '-');
          body.applicationUuid = investmentId.toString();
          body.transferApplication = this.transferApplication ?? '';
          body.transferId = this.transferId.toString() ?? "0";
          body.documentType = docType;
          return this.applicationService.uploadDocument(file, body);
        }),
        takeUntil(this.destroy$),
        finalize(() => this.isLoading = false)
      ).subscribe({
        next: async (res: responseModel) => {
          if (res.success) {
            this.loadOfflineDocuments();
          } else {
            const toast = await this.toastController.create({
              message: `${res.message}`,
              duration: 3000,
            });
            toast.present();
          }
        }
      })
    } else {

      const body = {} as any;
      body.groupid = "";
      body.documentType = docType;
      body.formid = "";
      body.holderUuid = this.investorId.toString() ?? "0";
      body.applicationUuid = this.applicationId.toString();
      body.pdfPageno = this.pdfPageNo;
      body.childfundid = this.childFundId;
      this.isLoading = true;
      this.uploadService.uploadDocuments(file, body)
        .pipe(
          switchMap((res: any) => {

            const bodysave = {} as any;
            bodysave.InvestorId = this.investorId;
            bodysave.InvestmentId = this.applicationId;
            bodysave.FileName = res.data;
            bodysave.documentType = docType;
            bodysave.PdfPageno = this.pdfPageNo;
            bodysave.TransferId = this.transferId;

            return this.uploadService.updateDocumentDetails(bodysave).pipe(
              map((updateRes: responseModel) => ({
                updateRes,
                fileName: bodysave.FileName
              }))
            );
          }),
          finalize(() => this.isLoading = false),
          takeUntil(this.destroy$)
        ).subscribe({
          next: async ({ updateRes, fileName }) => {
            if (updateRes.success === false) {
              const toast = await this.toastController.create({
                message: `${updateRes.message}`,
                duration: 3000,
              });
              toast.present();
              return;
            }
            this.loadOfflineDocuments();
          }
        })
    }


  }

  /**
   * Download document from S3 buckets
   * @param doc: TransferDocument
   * @returns void
   */
  downloadDocument(doc: TransferDocument) {
    this.sameDocumentTypeError = false;

    this.commonService.encrypt(this.applicationId)
      .pipe(
        switchMap((res: any) => {
          const investmentId = res.encryptedText.replaceAll('/', 'slh').replaceAll('==', '--').replaceAll('=', '-');
          if (doc.documentType === 'Application Form') {
            return this.applicationService.downloadDocument(doc.fileName, this.investorId, investmentId, doc.fileName, '0', '', doc.documentType)
          } else {
            return this.applicationService.downloadDocument(doc.documentName, this.investorId, investmentId, doc.fileName, this.transferApplication.toString(), "0", doc.documentType)
          }
        }),
        takeUntil(this.destroy$)
      )
      .subscribe((res: any) => {
        const docUrl = `${res.message}`;
        window.open(docUrl, '_blank');
      });

  }

  async deleteDocument(doc: TransferDocument) {
    this.sameDocumentTypeError = false;
    const alert = await this.alertController.create({
      header: 'Confirm!',
      message: `Are you sure you want to delete this document ?`,
      buttons: [
        {
          text: 'No',
          handler: () => { },
        },
        {
          text: 'Yes',
          handler: () => {
            this.applicationService
              .deleteDocument(doc.documentId, doc.fileName)
              .pipe(takeUntil(this.destroy$))
              .subscribe({
                next: async () => {
                  const toast = await this.toastController.create({
                    message: `Document ${doc.fileName} deleted successfully.`,
                    duration: 2000,
                  });
                  toast.present();
                  this.loadOfflineDocuments();
                },
              });
          },
        },
      ],
    });

    await alert.present();
  }

  async submitWetSignApplication() {

    //check if all documents have been uploaded
    const validationMessage = this.validateAllDocuments();

    if (validationMessage) {
      const toast = await this.toastController.create({
        message: validationMessage,
        duration: 3000
      })
      toast.present();
      return;
    }

    this.isLoading = true;
    this.applicationService.submitWetSignApplication({ InvestmentId: this.applicationId })
      .pipe(
        takeUntil(this.destroy$)
      )
      .subscribe({
        next: async () => {
          this.isLoading = false;
          const toast = await this.toastController.create({
            message: `Application submitted successfully.`,
            duration: 2000,
          });
          toast.present();
          this.dismissModal();
        }
      })
  }

  openConfirmModal(): void {

    this.dialogRef = this.dialog.open(this.confirmSubmissionModal, {
      width: '40%',
      disableClose: true,
      autoFocus: false,
      panelClass: 'modern-dialog'
    });
  }
  //#endregion

  //#region Private Functions
  private loadOfflineDocuments() {
    this.uploadService.getOfflineDocumentByInvestorId(this.applicationId)
      .pipe(
        takeUntil(this.destroy$)
      )
      .subscribe((res: Array<InvestorDocument>) => {
        this.uploadedDocuments = [...res.map((doc) => ({
          documentId: doc.documentId,
          documentName: doc.documentName,
          documentType: doc.documentType,
          fileName: doc.fileName,
          transferId: doc.transferId,
          originalDocument: null
        }))];
      });
  }

  private validateAllDocuments() {
    for (const type of this.requiredDocuments) {
      if (type.isMandatory) {
        const isUploaded = this.uploadedDocuments.some(x => x.documentType === type.value);
        if (!isUploaded)
          return `${type.name} is required`;
      }
    }

    return '';
  }
  //#endregion
}
